export type ImageFormat = {
  url: string
  width: number
  height: number
}

export type Image = {
  url: string
  width: number
  height: number
  alternativeText?: string | null
  formats?: Record<string, ImageFormat>
}

export type ImageCandidate = {
  url: string
  width: number
}

export type SizeRule = {
  maxWidth: number
  size: string
}

/**
 * Collect all available image sources (formats + original)
 * and normalize them into width-based candidates for srcset.
 */
const collectImageCandidates = (image: Image): ImageCandidate[] => {
  const candidates: ImageCandidate[] = []

  // Add resized formats generated by CMS (e.g. thumbnail, small, medium, large)
  if (image.formats) {
    Object.values(image.formats).forEach((format) => {
      if (format?.url && format?.width) {
        candidates.push({
          url: format.url,
          width: format.width,
        })
      }
    })
  }

  // Always include the original image as the largest fallback candidate
  if (image.url && image.width) {
    candidates.push({
      url: image.url,
      width: image.width,
    })
  }

  return candidates
}

/**
 * Build a valid srcset string using width descriptors (w).
 * Assumes width values are accurate and come from image metadata.
 */
export const buildSrcSet = (
  image?: Image,
  options?: {
    minWidth?: number
    maxWidth?: number
  }
): string | undefined => {
  if (!image) return undefined

  const { minWidth = 0, maxWidth = Infinity } = options ?? {}

  // Use Map to deduplicate candidates by width
  const uniqueByWidth = new Map<number, string>()

  collectImageCandidates(image)
    .filter((c) => c.width >= minWidth && c.width <= maxWidth)
    .forEach((c) => {
      // Browser fully trusts width descriptors, so duplicates must be avoided
      if (!uniqueByWidth.has(c.width)) {
        uniqueByWidth.set(c.width, c.url)
      }
    })

  if (uniqueByWidth.size === 0) return undefined

  // srcset must be sorted ascending by width to follow spec and browser expectations
  return Array.from(uniqueByWidth.entries())
    .sort(([a], [b]) => a - b)
    .map(([width, url]) => `${url} ${width}w`)
    .join(", ")
}

/**
 * Build the `sizes` attribute from breakpoint rules.
 * This describes the rendered image width, not the file size.
 */
export function buildSizes(rules: SizeRule[], fallback: string): string {
  const mediaRules = rules
    // Ensure rules are evaluated from smallest to largest viewport
    .sort((a, b) => a.maxWidth - b.maxWidth)
    .map((rule) => `(max-width: ${rule.maxWidth}px) ${rule.size}`)

  // The last value is the default when no media query matches
  return [...mediaRules, fallback].join(", ")
}
